import { AlertTriangle, ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { BreadthPressureStateDto, StockbeeBreadthRowDto } from 'shared';

import { useMarketPulse } from '../features/marketConsole/queries';
import { useReplayDate } from '../lib/useReplayDate';
import { cn } from '../lib/utils';

const UP_COLOR = '#10B981';
const DOWN_COLOR = '#EF4444';
const NEUTRAL_COLOR = '#60A5FA';
const CHART_SESSIONS = 90;
const TABLE_SESSIONS = 20;

type MetricKey = Exclude<keyof StockbeeBreadthRowDto, 'date'>;

interface ChartView {
  label: string;
  note: string;
  series: ReadonlyArray<{ key: MetricKey; name: string; color: string }>;
  referenceLine?: number;
}

const CHART_VIEWS = {
  daily: {
    label: 'Daily ±4%',
    note: 'Stocks up 4%+ versus down 4%+ each session: immediate buying versus selling pressure.',
    series: [
      { key: 'up4', name: 'Up 4%+', color: UP_COLOR },
      { key: 'down4', name: 'Down 4%+', color: DOWN_COLOR },
    ],
  },
  ratios: {
    label: '5D / 10D ratios',
    note: 'Up-4% events divided by down-4% events over 5 and 10 sessions. Above 1 favours buyers.',
    series: [
      { key: 'ratio5', name: '5-day', color: UP_COLOR },
      { key: 'ratio10', name: '10-day', color: NEUTRAL_COLOR },
    ],
    referenceLine: 1,
  },
  quarter: {
    label: 'Quarter ±25%',
    note: 'Stocks up 25%+ versus down 25%+ in a quarter: the medium-term momentum regime.',
    series: [
      { key: 'up25Quarter', name: 'Up 25%+', color: UP_COLOR },
      { key: 'down25Quarter', name: 'Down 25%+', color: DOWN_COLOR },
    ],
  },
  month: {
    label: 'Month ±25%',
    note: 'Large one-month moves in both directions.',
    series: [
      { key: 'up25Month', name: 'Up 25%+', color: UP_COLOR },
      { key: 'down25Month', name: 'Down 25%+', color: DOWN_COLOR },
    ],
  },
  intermediate: {
    label: '34D ±13%',
    note: 'Stocks up or down 13%+ in 34 days: intermediate participation.',
    series: [
      { key: 'up13In34Days', name: 'Up 13%+', color: UP_COLOR },
      { key: 'down13In34Days', name: 'Down 13%+', color: DOWN_COLOR },
    ],
  },
  t2108: {
    label: 'T2108',
    note: 'Percentage of stocks above their 40-day moving average.',
    series: [{ key: 't2108', name: 'T2108 %', color: NEUTRAL_COLOR }],
  },
} satisfies Record<string, ChartView>;

type ChartViewKey = keyof typeof CHART_VIEWS;
const CHART_VIEW_KEYS: readonly ChartViewKey[] = [
  'daily',
  'ratios',
  'quarter',
  'month',
  'intermediate',
  't2108',
];

const BALANCE_PAIRS: ReadonlyArray<{
  title: string;
  note: string;
  up: MetricKey;
  down: MetricKey;
}> = [
  {
    title: 'Quarter ±25%',
    note: 'Stocks 25%+ off a 65-day low versus 25%+ below a 65-day high.',
    up: 'up25Quarter',
    down: 'down25Quarter',
  },
  {
    title: 'Month ±25%',
    note: 'Strong one-month gainers versus strong one-month decliners.',
    up: 'up25Month',
    down: 'down25Month',
  },
  {
    title: 'Month ±50%',
    note: 'Rare, high-velocity monthly moves; useful for spotting extremes.',
    up: 'up50Month',
    down: 'down50Month',
  },
  {
    title: '34 days ±13%',
    note: 'A broader intermediate-momentum participation measure.',
    up: 'up13In34Days',
    down: 'down13In34Days',
  },
];

const PRESSURE_COPY: Record<
  BreadthPressureStateDto,
  { label: string; tone: 'success' | 'danger' | 'warn' }
> = {
  BUYING_PRESSURE: { label: 'Buying pressure', tone: 'success' },
  SELLING_PRESSURE: { label: 'Selling pressure', tone: 'danger' },
  MIXED: { label: 'Mixed / transition', tone: 'warn' },
};

const toneText = { success: 'text-success', danger: 'text-danger', warn: 'text-warn' } as const;

function formatInteger(value: number | null): string {
  return value === null ? '—' : value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatRatio(value: number | null): string {
  return value === null ? '—' : value.toFixed(2);
}

function formatSigned(value: number | null, digits: number, suffix = ''): string {
  if (value === null) return '—';
  const rounded = value.toFixed(digits);
  return `${value > 0 ? '+' : ''}${rounded}${suffix}`;
}

function net(row: StockbeeBreadthRowDto): number | null {
  return row.up4 === null || row.down4 === null ? null : row.up4 - row.down4;
}

function ratioTone(value: number | null): string {
  if (value === null) return 'text-text-muted';
  return value >= 1 ? 'text-success' : 'text-danger';
}

function changeTone(value: number | null): string {
  if (value === null || value === 0) return 'text-text-muted';
  return value > 0 ? 'text-success' : 'text-danger';
}

function DirectionIcon({ value }: { value: number | null }) {
  if (value === null || value === 0) return <Minus className="h-4 w-4" aria-hidden="true" />;
  return value > 0 ? (
    <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
  ) : (
    <ArrowDownRight className="h-4 w-4" aria-hidden="true" />
  );
}

function Tile({ children, label, note }: { children: ReactNode; label: string; note?: string }) {
  return (
    <article className="rounded-lg border border-border bg-surface p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className="mt-1 font-mono text-2xl font-semibold tabular-nums">{children}</div>
      {note ? <p className="mt-1 text-xs text-text-muted">{note}</p> : null}
    </article>
  );
}

function BalanceCard({
  row,
  pair,
}: {
  row: StockbeeBreadthRowDto;
  pair: (typeof BALANCE_PAIRS)[number];
}) {
  const up = row[pair.up];
  const down = row[pair.down];
  const total = Math.max((up ?? 0) + (down ?? 0), 1);
  return (
    <article className="rounded-lg border border-border bg-surface p-4">
      <h3 className="text-sm font-semibold">{pair.title}</h3>
      <p className="mt-1 text-xs text-text-muted">{pair.note}</p>
      <dl className="mt-3 grid grid-cols-2 gap-2 font-mono tabular-nums">
        <div className="rounded bg-bg-muted p-2">
          <dt className="text-[11px] text-text-muted">Up</dt>
          <dd className="text-lg font-semibold text-success">{formatInteger(up)}</dd>
        </div>
        <div className="rounded bg-bg-muted p-2">
          <dt className="text-[11px] text-text-muted">Down</dt>
          <dd className="text-lg font-semibold text-danger">{formatInteger(down)}</dd>
        </div>
      </dl>
      <div
        className="mt-3 flex h-2 overflow-hidden rounded-full bg-bg-muted"
        role="img"
        aria-label={`${pair.title}: ${formatInteger(up)} up versus ${formatInteger(down)} down`}
      >
        <div className="bg-success" style={{ width: `${((up ?? 0) / total) * 100}%` }} />
        <div className="bg-danger" style={{ width: `${((down ?? 0) / total) * 100}%` }} />
      </div>
    </article>
  );
}

function BreadthChart({ rows }: { rows: readonly StockbeeBreadthRowDto[] }) {
  const [viewKey, setViewKey] = useState<ChartViewKey>('daily');
  const view: ChartView = CHART_VIEWS[viewKey];
  const data = rows.slice(0, CHART_SESSIONS).reverse();
  return (
    <section
      className="rounded-lg border border-border bg-surface p-4"
      aria-labelledby="pulse-chart-heading"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 id="pulse-chart-heading" className="text-sm font-semibold">
            {view.label}
          </h2>
          <p className="mt-1 text-xs text-text-muted">{view.note}</p>
        </div>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Breadth chart view">
          {CHART_VIEW_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={key === viewKey}
              onClick={() => setViewKey(key)}
              className={cn(
                'rounded border px-2.5 py-1.5 text-xs font-medium transition',
                key === viewKey
                  ? 'border-text bg-text text-bg'
                  : 'border-border bg-bg-muted text-text-muted hover:text-text',
              )}
            >
              {CHART_VIEWS[key].label}
            </button>
          ))}
        </div>
      </div>
      <div
        className="mt-3 h-64"
        role="img"
        aria-label={`${view.label} over the last ${data.length} sessions`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
            <YAxis tick={{ fontSize: 11 }} width={44} />
            <Tooltip />
            {view.referenceLine !== undefined ? (
              <ReferenceLine y={view.referenceLine} stroke="#F59E0B" strokeDasharray="4 4" />
            ) : null}
            {view.series.map((series) => (
              <Line
                key={series.key}
                type="monotone"
                dataKey={series.key}
                name={series.name}
                stroke={series.color}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function SpxChart({ rows }: { rows: readonly StockbeeBreadthRowDto[] }) {
  const data = rows.slice(0, CHART_SESSIONS).reverse();
  return (
    <section
      className="rounded-lg border border-border bg-surface p-4"
      aria-labelledby="pulse-spx-heading"
    >
      <h2 id="pulse-spx-heading" className="text-sm font-semibold">
        S&amp;P 500
      </h2>
      <p className="mt-1 text-xs text-text-muted">Index close over the same sessions.</p>
      <div className="mt-3 h-48" role="img" aria-label="S&P 500 close over the same sessions">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
            <YAxis tick={{ fontSize: 11 }} width={52} domain={['auto', 'auto']} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="spClose"
              name="S&P 500"
              stroke={NEUTRAL_COLOR}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function RecentSessions({ rows }: { rows: readonly StockbeeBreadthRowDto[] }) {
  const recent = rows.slice(0, TABLE_SESSIONS);
  return (
    <section
      className="rounded-lg border border-border bg-surface"
      aria-labelledby="pulse-sessions-heading"
    >
      <h2
        id="pulse-sessions-heading"
        className="border-b border-border px-4 py-3 text-sm font-semibold"
      >
        Recent sessions
      </h2>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm tabular-nums">
          <thead className="text-xs text-text-muted">
            <tr className="border-b border-border">
              <th scope="col" className="px-4 py-2 text-left font-medium">
                Date
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Up 4%
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Down 4%
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Net
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                5D
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                10D
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Qtr +25%
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Qtr −25%
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                T2108
              </th>
              <th scope="col" className="px-4 py-2 text-right font-medium">
                S&amp;P 500
              </th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {recent.map((row) => (
              <tr key={row.date} className="border-b border-border last:border-0">
                <td className="px-4 py-2 text-left font-sans">{row.date}</td>
                <td className="px-3 py-2 text-right text-success">{formatInteger(row.up4)}</td>
                <td className="px-3 py-2 text-right text-danger">{formatInteger(row.down4)}</td>
                <td className={cn('px-3 py-2 text-right', changeTone(net(row)))}>
                  {formatSigned(net(row), 0)}
                </td>
                <td className={cn('px-3 py-2 text-right', ratioTone(row.ratio5))}>
                  {formatRatio(row.ratio5)}
                </td>
                <td className={cn('px-3 py-2 text-right', ratioTone(row.ratio10))}>
                  {formatRatio(row.ratio10)}
                </td>
                <td className="px-3 py-2 text-right">{formatInteger(row.up25Quarter)}</td>
                <td className="px-3 py-2 text-right">{formatInteger(row.down25Quarter)}</td>
                <td className="px-3 py-2 text-right">
                  {row.t2108 === null ? '—' : `${row.t2108.toFixed(1)}%`}
                </td>
                <td className="px-4 py-2 text-right">
                  {row.spClose === null
                    ? '—'
                    : row.spClose.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="divide-y divide-border sm:hidden">
        {recent.map((row) => (
          <li key={row.date} className="px-4 py-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">{row.date}</span>
              <span className={cn('font-mono tabular-nums', changeTone(net(row)))}>
                Net {formatSigned(net(row), 0)}
              </span>
            </div>
            <dl className="mt-2 grid grid-cols-4 gap-2 font-mono text-xs tabular-nums">
              <div>
                <dt className="font-sans text-text-muted">Up 4%</dt>
                <dd className="text-success">{formatInteger(row.up4)}</dd>
              </div>
              <div>
                <dt className="font-sans text-text-muted">Down 4%</dt>
                <dd className="text-danger">{formatInteger(row.down4)}</dd>
              </div>
              <div>
                <dt className="font-sans text-text-muted">10D</dt>
                <dd className={ratioTone(row.ratio10)}>{formatRatio(row.ratio10)}</dd>
              </div>
              <div>
                <dt className="font-sans text-text-muted">T2108</dt>
                <dd>{row.t2108 === null ? '—' : `${row.t2108.toFixed(1)}%`}</dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function MarketPulse() {
  const pulse = useMarketPulse(useReplayDate().asOf);

  if (pulse.isLoading) {
    return <main className="p-6 text-text-muted">Loading market pulse…</main>;
  }
  if (pulse.isError || !pulse.data) {
    return <main className="p-6 text-danger">Market pulse data could not be loaded.</main>;
  }

  const data = pulse.data;
  const latest = data.latest;

  return (
    <main className="text-text">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-5 sm:px-6">
        <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Pulse</h1>
            <p className="mt-1 text-sm text-text-muted">
              How much of the market is participating, not just what the index is doing.
            </p>
          </div>
          {data.asOfDate ? (
            <div className="text-xs text-text-muted">
              Close · {data.asOfDate} · Informational only
            </div>
          ) : null}
        </header>

        {data.status === 'NOT_AVAILABLE' || !latest ? (
          <section className="flex gap-3 rounded-lg border border-warn/30 bg-warn/10 p-4 text-sm text-warn">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>
              Breadth data has not been captured yet. Run the Stockbee breadth refresh in{' '}
              <Link to="/settings/market-data" className="underline">
                Settings → Market data
              </Link>
              .
            </p>
          </section>
        ) : (
          <>
            <section
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
              aria-label="Current breadth read"
            >
              <article className="rounded-lg border border-border bg-surface p-4 sm:col-span-2 lg:col-span-1">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  Current read
                </div>
                {data.pressure ? (
                  <>
                    <div
                      className={cn(
                        'mt-1 text-xl font-semibold',
                        toneText[PRESSURE_COPY[data.pressure.state].tone],
                      )}
                    >
                      {PRESSURE_COPY[data.pressure.state].label}
                    </div>
                    <p className="mt-1 text-xs text-text-muted">{data.pressure.reason}</p>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-text-muted">
                    Not enough data to classify this session.
                  </p>
                )}
              </article>
              <Tile label="Today: up 4% / down 4%" note={`Net ${formatSigned(net(latest), 0)}`}>
                <span className="text-success">{formatInteger(latest.up4)}</span>
                <span className="text-text-muted"> / </span>
                <span className="text-danger">{formatInteger(latest.down4)}</span>
              </Tile>
              <Tile label="5-day ratio" note="Up-4% ÷ down-4% events over 5 sessions">
                <span className={ratioTone(latest.ratio5)}>{formatRatio(latest.ratio5)}</span>
              </Tile>
              <Tile label="10-day ratio" note="Smoother confirmation of the short-term tape">
                <span className={ratioTone(latest.ratio10)}>{formatRatio(latest.ratio10)}</span>
              </Tile>
              <Tile label="T2108" note="Stocks above their 40-day average">
                {latest.t2108 === null ? '—' : `${latest.t2108.toFixed(1)}%`}
              </Tile>
            </section>

            {data.sincePriorSession ? (
              <section
                className="rounded-lg border border-border bg-surface p-4"
                aria-labelledby="pulse-since-prior-heading"
              >
                <h2 id="pulse-since-prior-heading" className="text-sm font-semibold">
                  Since prior close ({data.sincePriorSession.priorDate})
                </h2>
                <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    {
                      label: '±4% net',
                      value: data.sincePriorSession.net4Change,
                      text: formatSigned(data.sincePriorSession.net4Change, 0),
                    },
                    {
                      label: 'T2108',
                      value: data.sincePriorSession.t2108Change,
                      text: formatSigned(data.sincePriorSession.t2108Change, 1, ' pp'),
                    },
                    {
                      label: '10-day ratio',
                      value: data.sincePriorSession.ratio10Change,
                      text: formatSigned(data.sincePriorSession.ratio10Change, 2),
                    },
                  ].map((item) => (
                    <div key={item.label} className="rounded bg-bg-muted p-3">
                      <dt className="text-[11px] text-text-muted">{item.label}</dt>
                      <dd
                        className={cn(
                          'mt-1 flex items-center gap-1 font-mono text-lg font-semibold tabular-nums',
                          changeTone(item.value),
                        )}
                      >
                        <DirectionIcon value={item.value} />
                        {item.text}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}

            <section
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
              aria-label="Momentum balance"
            >
              {BALANCE_PAIRS.map((pair) => (
                <BalanceCard key={pair.title} pair={pair} row={latest} />
              ))}
            </section>

            <BreadthChart rows={data.rows} />
            <SpxChart rows={data.rows} />
            <RecentSessions rows={data.rows} />
          </>
        )}

        <p className="text-xs text-text-muted">
          Data:{' '}
          <a href={data.source.url} target="_blank" rel="noopener noreferrer" className="underline">
            {data.source.name}
          </a>
          . Breadth describes participation; it is context, not a trade signal.
        </p>
      </div>
    </main>
  );
}
